import { CameraControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import Loader from "../Loader/Loader";

import { extend } from "@react-three/fiber";
import { geometry } from "maath";

import "./styles/characterVisualiser.style.sass";
import "../Forms/styles/form.sass";

import {
    overwriteCharacterVersion,
    parseTraits,
    setVersionScales,
    transformToCharacterListVersion,
} from "@src/utils/character-utils";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    XCircleIcon,
} from "@heroicons/react/24/solid";
import ExpressionSelector from "../ExpressionSelector/ExpressionSelector";
import CharacterVisualiserUi from "../CharacterVisualiserUi/CharacterVisualiserUI";
import {
    EffectComposer,
    HueSaturation,
    Vignette,
} from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
import Button from "../Button/Button";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import VersionForm from "../Forms/CharacterForm/VersionForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@src/utils/axios-instance";
import { AxiosResponse } from "axios";
import { useCharacterStore } from "@src/stores/characterStore";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useBlocker } from "react-router";
import Modal from "../Modal/Modal";
import CharacterMetaForm from "../Forms/CharacterForm/CharacterMetaForm";
import VersionManager from "../VersionManager/VersionManager";
import CharacterFeatures from "../CharacterFeatures/CharacterFeatures";
import Sprite from "../Sprite/Sprite";
import {
    Character,
    Expression,
    Preferences,
    Version,
    VersionDTO,
} from "@src/types/global";

extend({ RoundedPlaneGeometry: geometry.RoundedPlaneGeometry });
interface IProps {
    characterData: Character;
    queriedVersions: Version[];
}

// We need to give RHF the entire object so that switching from one version to the other doesn't keep unwanted values
const dummyVersion = {
    version_name: "In progress...",
    body: "https://ownidev.syd1.digitaloceanspaces.com/uploads/no_image_found.png",
    height: 170,
    age: "",
    ears: null,
    eyes: null,
    faceshape: null,
    mouth: null,
    nose: null,
    about: "",
    pronouns: null,
    flaws: ["", "", ""],
    qualities: ["", "", ""],
} as any as Version;
const metaSchema = yup.object().shape({
    is_public: yup.boolean().default(false).required(),
    first_name: yup
        .string()
        .min(1)
        .max(250)
        .required({ msg: "Please provide a first name." }),
    last_name: yup
        .string()
        .min(1)
        .max(250)
        .required({ msg: "Please provide a last name." }),
    avatar: yup.mixed(),
});
const schema = yup.object().shape({
    id: yup.string(),
    version_name: yup.string().min(1).max(250).default("Default").required(),
    is_default: yup.boolean().required(),
    is_nsfw: yup.boolean().required(),
    age: yup.string().min(0).required(),
    ears: yup.string().min(0).max(250).nullable(),
    eyes: yup.string().min(0).max(250).nullable(),
    faceshape: yup.string().max(250).nullable(),
    mouth: yup.string().max(250).nullable(),
    nose: yup.string().max(250).nullable(),
    about: yup.string().max(500).nullable(),
    body: yup
        .mixed()
        .transform((_, value) => {
            return value ===
                "https://ownidev.syd1.digitaloceanspaces.com/uploads/no_image_found.png"
                ? null
                : value;
        })
        .required("Please provide an image"),
    height: yup.number().required(),
    override_height: yup
        .number()
        .transform((_, value) => {
            return value == Number(value) ? Number(value) : null;
        })
        .nullable()
        .label("Override height"),
    pronouns: yup
        .string()
        .min(1, "Please provide pronouns (or lackthereof). Mininum 1 character")
        .max(125)
        .required({ msg: "Please provide pronouns (or lackthereof)" }),
    flaws: yup.array(yup.string().nullable()),
    qualities: yup.array(yup.string().nullable()),
});
const CharacterVisualiser = React.memo(
    ({ characterData, queriedVersions }: IProps) => {
        const {
            selectedVersion,
            setSelectedVersion,
            setSelectedExpression,
            updateCharacterVersionInList,
        } = useCharacterStore((state) => {
            return {
                selectedVersion: state.selectedVersion,
                setSelectedVersion: state.setSelectedVersion,
                selectedExpression: state.setSelectedExpression,
                setSelectedExpression: state.setSelectedExpression,
                updateCharacterVersionInList:
                    state.updateCharacterVersionInList,
            };
        });
        const [character, setCharacter] = useState(characterData);
        useEffect(() => {
            setCharacter(characterData);
            if (queriedVersions.length == versions.length) {
                setVersions(queriedVersions);
            }
        }, [characterData, queriedVersions]);
        const [versions, setVersions] = useState<Version[]>(queriedVersions);
        const [openedTab, setOpenedTab] = useState<"characterMeta" | "version">(
            "version",
        );
        const [sprites, setSprites] = useState<
            { sprite: React.ReactNode; pos: number[] }[]
        >([]);
        const [formOpen, setFormOpen] = useState(false);
        const cameraControlRef = useRef<CameraControls | null>(null);
        const [zoomedIn, setZoomedIn] = useState(false);

        const scaledVersions = useMemo(
            () => setVersionScales(versions, versions[selectedVersion]),
            [selectedVersion, versions],
        );
        const metaMethods = useForm({
            defaultValues: character,
            resolver: yupResolver(metaSchema),
        });
        const methods = useForm({
            defaultValues: dummyVersion,
            //@ts-expect-error
            resolver: yupResolver(schema),
        });
        const blocker = useBlocker(
            ({ currentLocation, nextLocation }) =>
                methods.formState.isDirty &&
                currentLocation.pathname !== nextLocation.pathname,
        );
        const watcher = useWatch({ control: methods.control });
        const queryClient = useQueryClient();
        const userPref = queryClient.getQueryData(["userPref"]) as Preferences;
        async function createVersion(data: VersionDTO): Promise<Version> {
            const { body, ...untypedVersion } = data;
            const bodyForm = new FormData();
            bodyForm.append("body", body);
            const version = untypedVersion as any as Version;

            untypedVersion.qualities !== undefined &&
            Array.isArray(untypedVersion.qualities)
                ? (version.qualities = untypedVersion.qualities.join(","))
                : (version.qualities = "");
            untypedVersion.flaws !== undefined &&
            Array.isArray(untypedVersion.flaws)
                ? (version.flaws = untypedVersion.flaws.join(","))
                : (version.flaws = "");

            try {
                let versionResult = {} as AxiosResponse<{ data: Version }>;
                if (version.id != null && version.id !== "") {
                    versionResult = await api.patch(
                        "/characters/" +
                            character.id +
                            "/version/" +
                            version.id,
                        {
                            character_version: version,
                        },
                    );
                } else {
                    versionResult = await api.post(
                        "/characters/" + character.id + "/version",
                        {
                            character_version: version,
                        },
                    );
                }
                //@ts-ignore
                if (body instanceof File) {
                    const res = await api.patch(
                        `characters/${character.id}/version/${versionResult.data.data.id}/upload`,
                        bodyForm,
                        {
                            headers: {
                                "Content-Type": "multipart/form-data",
                            },
                        },
                    );
                    if (res) {
                        return res.data;
                    } else {
                        throw new Error("No data returned from server");
                    }
                }
                return versionResult.data.data;
            } catch (err: any) {
                console.log(err);
                throw new Error("No data returned from server");
            }
        }
        const updateCharacterVersion = (data: Version) => {
            queryClient.setQueryData(
                ["characters", character.id],
                (character: Character) => {
                    return overwriteCharacterVersion(character, data);
                },
            );
            queryClient.setQueryData(
                ["characters", character.id, "versions"],
                (prevVersions: Version[]) => {
                    // Get the version
                    const vIndex = prevVersions.findIndex(
                        (v) => v.id == data.id,
                    );
                    if (vIndex < 0) return;
                    prevVersions[vIndex] = data;
                    return prevVersions;
                },
            );
            const characterWithVersion = {
                ...character,
                character_versions: data,
            };
            const characterListVersion =
                transformToCharacterListVersion(characterWithVersion);
            updateCharacterVersionInList(characterListVersion);
            const prevVersions = [...versions];
            prevVersions[selectedVersion] = data;
            setVersions(prevVersions);
        };
        const versionMutate = useMutation({
            mutationKey: ["characters", character.id],
            mutationFn: createVersion,
            onSuccess: (data: Version) => {
                updateCharacterVersion(data);
                setFormOpen(false);
                toast.success("Version created/ updated");
            },
            onError: (error) => {
                console.error(error);
                toast.error("Something happened ! Try again later ?");
            },
        });
        /**
         * Updates a character (meta only) given the form data
         * @param data - The character Data
         */
        async function updateCharacter(data: Character): Promise<Character> {
            const {
                avatar,
                default_version,
                character_versions,
                ...characterData
            } = data;
            const avatarForm = new FormData();
            avatarForm.append("avatar", avatar);
            let res = {} as AxiosResponse<{ data: Character }>;
            if (metaMethods.formState.dirtyFields.avatar) {
                res = (await api
                    .patch("/characters/" + character.id, avatarForm, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .catch(() => {
                        toast.error("The avatar couldn't be updated");
                    })) as AxiosResponse<{ data: Character }>;
            }
            res = (await api
                .patch("/characters/" + character.id, {
                    character: characterData,
                })
                .catch(() => {
                    toast.error(
                        "An error happened while updating this character !",
                    );
                })) as AxiosResponse<{ data: Character }>;
            return res.data.data;
        }
        const metaMutate = useMutation({
            mutationKey: ["characters", character.id],
            mutationFn: updateCharacter,
            onSuccess: (data: Character) => {
                queryClient.setQueryData(["characters", character.id], () => {
                    const character = data;
                    return character;
                });
                setFormOpen(false);
                toast.success("Character updated");
            },
            onError: () => {
                toast.error("Something happened ! Try again later ?");
            },
        });

        async function submit(data: VersionDTO) {
            versionMutate.mutate(data);
        }

        async function submitMeta(data: Character) {
            metaMutate.mutate(data);
        }

        function prevVer() {
            if (selectedVersion > 0) {
                rotateTo(selectedVersion - 1);
                setSelectedVersion(selectedVersion - 1);
                if (formOpen) {
                    const toForm = parseTraits(versions[selectedVersion - 1]);
                    methods.reset(toForm, {
                        keepValues: false,
                    });
                }
            }
        }

        function nextVer() {
            if (selectedVersion < versions.length! - 1) {
                rotateTo(selectedVersion + 1);
                setSelectedVersion(selectedVersion + 1);
                if (formOpen) {
                    const toForm = parseTraits(versions[selectedVersion + 1]);
                    methods.reset(toForm, {
                        keepValues: false,
                    });
                }
            }
        }
        function rotateTo(index: number) {
            cameraControlRef.current?.zoomTo(1);
            setZoomedIn(false);
            const origin = new THREE.Vector3(0, 0, 2);
            const target = new THREE.Vector3(
                sprites[index].pos[0],
                0,
                sprites[index].pos[2],
            );
            const deg = origin.angleTo(target);

            const originalPosition = new THREE.Vector3();
            cameraControlRef.current?.getPosition(originalPosition);

            cameraControlRef.current
                ?.setPosition(originalPosition.x, 0, originalPosition.z)
                .then(() => {
                    cameraControlRef.current?.rotateAzimuthTo(-deg, true);
                })
                .finally(() => {
                    cameraControlRef.current?.setTarget(0, 0, 0, true);
                });
        }
        /*
         * zooms to and look at the top of a version's sprite
         */
        function zoomTo() {
            setZoomedIn(true);
            const originalPosition = new THREE.Vector3();
            cameraControlRef.current?.getPosition(originalPosition);
            if (originalPosition.y <= 1) {
                cameraControlRef.current
                    ?.setTarget(0, 2, 0)
                    .then(() => {
                        cameraControlRef.current?.setPosition(
                            originalPosition.x,
                            1,
                            originalPosition.z,
                            true,
                        );
                    })
                    .finally(() => {
                        cameraControlRef.current?.zoomTo(2, true);
                    });
            }
        }
        function zoomBack() {
            setZoomedIn(false);

            const originalPosition = new THREE.Vector3();
            cameraControlRef.current?.getPosition(originalPosition);

            cameraControlRef.current
                ?.setPosition(originalPosition.x, 0, originalPosition.z)
                .then(() => {
                    cameraControlRef.current?.setTarget(0, 0, 0, true);
                })
                .finally(() => {
                    cameraControlRef.current?.zoomTo(1, false);
                });
        }
        function handleExpressionSelect(exp: Expression) {
            setSelectedExpression(exp.image as any as string);
        }
        interface ScaledVersion extends Version {
            scale: number;
        }
        function setupSprite(ver: ScaledVersion, index: number) {
            const radius = 2;
            const angle = index + Math.PI * 0.5;
            const posX = Math.cos(angle) * radius;
            const posZ = Math.sin(angle) * radius;
            return {
                sprite: (
                    <Sprite
                        position={[posX, 0, posZ]}
                        url={ver.body}
                        key={index + ver.id}
                        scale={scaledVersions[index].scale}
                        index={index}
                        onClick={() => {
                            //setSelectedVersion(index);
                            if (zoomedIn) {
                                zoomBack();
                            }
                            if (!zoomedIn) {
                                zoomTo();
                            }
                        }}
                    />
                ),
                pos: [posX, 0, posZ],
            };
        }

        useEffect(() => {
            if (methods.formState.isDirty) {
                let newState = versions as VersionDTO[];
                let value = structuredClone(watcher);
                if (value.body != null && typeof value.body == "object") {
                    // Transform the value to DataURL
                    value.body = URL.createObjectURL(value.body);
                }
                if (value.flaws != null && Array.isArray(value.flaws)) {
                    value.flaws = value.flaws.join(",");
                } else {
                    value.flaws = [];
                }
                if (value.qualities != null && Array.isArray(value.qualities)) {
                    value.qualities = value.qualities.join(",");
                } else {
                    value.qualities = [];
                }
                //@ts-ignore
                newState[selectedVersion] = value;
                setVersions([...newState] as Version[]);
            }
            return () => {
                if (methods.formState.isDirty) {
                    window.onbeforeunload = () => true;
                }
            };
        }, [watcher]);

        useMemo(() => {
            setSprites(
                scaledVersions.map((ver, index) => {
                    return setupSprite(ver, index);
                }),
            );
        }, [versions, selectedVersion]);
        useEffect(() => {
            if (sprites.length === versions.length) {
                rotateTo(selectedVersion);
                console.log(selectedVersion);
            }
        }, [sprites]);

        return (
            <div className="character-visualiser">
                <Suspense fallback={<Loader />}>
                    <Canvas camera={{ near: 0.00001, fov: 90 }}>
                        <CameraControls ref={cameraControlRef} />
                        <group position={[0, -2, 0]}>
                            {sprites.map((s) => s.sprite)}
                        </group>
                        <EffectComposer
                            multisampling={0}
                            enableNormalPass={false}
                        >
                            <HueSaturation // TODO - Map User Scene Presets to this
                                blendFunction={BlendFunction.NORMAL} // blend mode
                                hue={0} // hue in radians
                                saturation={0} // saturation in radians
                            />

                            <Vignette
                                eskil={false}
                                offset={0.5}
                                darkness={0.5}
                            />
                        </EffectComposer>
                    </Canvas>
                </Suspense>
                {zoomedIn && (
                    <ExpressionSelector
                        version={versions[selectedVersion]}
                        onClick={handleExpressionSelect}
                    />
                )}

                {zoomedIn && (
                    <CharacterFeatures
                        features={{
                            eyes: versions[selectedVersion].eyes,
                            nose: versions[selectedVersion].nose,
                            mouth: versions[selectedVersion].mouth,
                            ears: versions[selectedVersion].ears,
                            faceshape: versions[selectedVersion].faceshape,
                        }}
                    />
                )}
                {!zoomedIn && <div>Display Outfits</div>}

                <CharacterVisualiserUi
                    character={characterData}
                    key={versions[selectedVersion].id}
                    selectedVersion={versions[selectedVersion]}
                    toggleZoom={() => {
                        if (zoomedIn) {
                            zoomBack();
                        }

                        if (!zoomedIn) {
                            zoomTo();
                        }
                        setZoomedIn(!zoomedIn);
                    }}
                    zoomedIn={zoomedIn}
                    formControl={() => {
                        if (formOpen) {
                            setFormOpen(false);
                            return;
                        }
                        const toForm = parseTraits(versions[selectedVersion]);
                        methods.reset(toForm, { keepValues: false });
                        setFormOpen(true);
                    }}
                    measurementUnit={userPref?.measurement_unit}
                />
                <div className="character-visualiser__version version_switcher">
                    {/*The name of the functions are swapped because we display the versions in reverse order */}
                    <button
                        aria-label="Previous version"
                        onClick={nextVer}
                        className={`character-visualiser__version__button${
                            selectedVersion >= versions.length - 1
                                ? "--disabled"
                                : ""
                        }`}
                    >
                        <ChevronLeftIcon className="icon" />
                    </button>
                    <span className={`character-visualiser__version__info`}>
                        {versions[selectedVersion].version_name}
                    </span>
                    {/*The name of the functions are swapped because we display the versions in reverse order */}
                    <button
                        aria-label="Next version"
                        onClick={prevVer}
                        className={`character-visualiser__version__button${
                            selectedVersion <= 0 == true ? "--disabled" : ""
                        }`}
                    >
                        <ChevronRightIcon className="icon" />
                    </button>
                </div>
                <VersionManager
                    addVersionFn={() => {
                        const newState = [
                            ...(versions as Version[]),
                            dummyVersion,
                        ];
                        setVersions(newState);
                        // Add that version to the query
                        queryClient.setQueryData(
                            ["characters", character.id, "versions"],
                            () => newState,
                        );
                        methods.reset(dummyVersion, { keepValues: false });
                        setSelectedVersion(versions.length);
                        setFormOpen(true);
                    }}
                    removeVersionFn={() => {
                        setVersions((oldState) => {
                            return oldState.filter(
                                (_v, index) => index != selectedVersion,
                            );
                        });
                        setFormOpen(false);
                        setSelectedVersion(selectedVersion - 1);
                    }}
                    version={versions[selectedVersion]}
                />
                {blocker.state === "blocked" ? (
                    <Modal
                        isOpen={blocker.state === "blocked"}
                        closeModalFn={() => blocker.reset()}
                    >
                        You have unsaved changes. Are you sure you want to leave
                        this page ?
                        <Button preset="stealth" fn={() => blocker.proceed()}>
                            Yes
                        </Button>
                        <Button preset="secondary" fn={() => blocker.reset()}>
                            No
                        </Button>
                    </Modal>
                ) : null}
                {formOpen && (
                    <div className="form__container">
                        <div className="form__container__buttons">
                            <Button
                                preset={
                                    openedTab == "characterMeta"
                                        ? "primary"
                                        : "secondary"
                                }
                                fn={() => setOpenedTab("characterMeta")}
                            >
                                Character Meta
                            </Button>

                            <Button
                                preset={
                                    openedTab == "version"
                                        ? "primary"
                                        : "secondary"
                                }
                                fn={() => setOpenedTab("version")}
                            >
                                Version
                            </Button>

                            <button
                                className="icon--lg"
                                aria-label="Close form"
                                onClick={() => setFormOpen(false)}
                                title="Close form"
                            >
                                <XCircleIcon />
                            </button>
                        </div>
                        {openedTab == "version" ? (
                            <FormProvider {...methods}>
                                <form onSubmit={methods.handleSubmit(submit)}>
                                    <VersionForm
                                        key={userPref?.measurement_unit}
                                        hints={true}
                                        bodyImage={
                                            versions[selectedVersion].body
                                        }
                                        measurementUnit={
                                            userPref?.measurement_unit ||
                                            "metric"
                                        }
                                    />
                                    <Button
                                        isLoading={versionMutate.isPending}
                                        type="submit"
                                        preset="primary"
                                    >
                                        Submit
                                    </Button>
                                </form>
                            </FormProvider>
                        ) : (
                            <FormProvider {...metaMethods}>
                                <form
                                    onSubmit={metaMethods.handleSubmit(
                                        //@ts-expect-error Yes, I know
                                        submitMeta,
                                    )}
                                >
                                    <CharacterMetaForm
                                        avatar={character.avatar}
                                    />
                                    <Button
                                        isLoading={metaMutate.isPending}
                                        type="submit"
                                        preset="primary"
                                    >
                                        Submit
                                    </Button>
                                </form>
                            </FormProvider>
                        )}
                    </div>
                )}
            </div>
        );
    },
);
export default CharacterVisualiser;

//https://discourse.threejs.org/t/scaling-sprites-to-original-image-size/55471
